import Component from "@glimmer/component";
import { array, concat, fn } from "@ember/helper";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import { modifier } from "ember-modifier";
import DMenu from "discourse/float-kit/components/d-menu";
import { iconHTML } from "discourse/lib/icon-library";
import { translateModKey } from "discourse/lib/utilities";
import { PLATFORM_KEY_MODIFIER } from "discourse/services/keyboard-shortcuts";
import DButton from "discourse/ui-kit/d-button";
import DDropdownMenu from "discourse/ui-kit/d-dropdown-menu";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";

export default class ToolbarPopupMenuOptions extends Component {
  @service capabilities;

  trackScrollability = modifier((element) => {
    const innerContent = element.closest(".fk-d-menu__inner-content");
    const menuElement = innerContent?.parentElement;
    if (!innerContent || !menuElement) {
      return;
    }

    const checkScroll = () => {
      const { scrollHeight, scrollTop, clientHeight } = innerContent;
      const hasOverflow = scrollHeight > clientHeight;
      menuElement.classList.toggle(
        "--scroll-top",
        hasOverflow && scrollTop > 2
      );
      menuElement.classList.toggle(
        "--scroll-bottom",
        hasOverflow && scrollHeight - scrollTop - clientHeight > 2
      );
    };

    const observer = new ResizeObserver(checkScroll);
    observer.observe(innerContent);
    innerContent.addEventListener("scroll", checkScroll, { passive: true });
    checkScroll();

    return () => {
      observer.disconnect();
      innerContent.removeEventListener("scroll", checkScroll);
    };
  });

  willDestroy() {
    super.willDestroy();
    this.dMenu?.destroy();
  }

  @action
  async onSelect(option) {
    await this.dMenu?.close();
    this.args.onChange?.(option);
  }

  @action
  onRegisterApi(api) {
    this.dMenu = api;
  }

  #convertMenuOption(content) {
    if (content.condition !== false) {
      const label = this.#calculateLabel(content);
      const title = this.#calculateTitle(content);

      return Object.defineProperties(
        {},
        Object.getOwnPropertyDescriptors({ ...content, label, title })
      );
    }
  }

  #calculateTitle(content) {
    if (content.label && !content.title && !content.translatedTitle) {
      return this.#calculateLabel(content, { textOnly: true });
    }

    if (!content.translatedTitle && !content.title) {
      return;
    }

    const title = content.translatedTitle
      ? content.translatedTitle
      : i18n(content.title);

    if (content.shortcut) {
      return `${title} (${translateModKey(
        PLATFORM_KEY_MODIFIER + "+" + content.shortcut
      )})`;
    }

    return title;
  }

  #calculateLabel(content, opts = {}) {
    if (!content.label && !content.translatedLabel) {
      return;
    }

    const label = content.translatedLabel
      ? content.translatedLabel
      : i18n(content.label);

    if (opts.textOnly) {
      if (content.shortcut) {
        return `${label} (${translateModKey(
          PLATFORM_KEY_MODIFIER + "+" + content.shortcut
        )})`;
      }

      return label;
    }

    let htmlLabel = `<span class="d-button-label__text">${label}</span>`;
    if (content.shortcut) {
      const separator = this.capabilities.isApple ? "" : " ";
      const platformClass = this.capabilities.isApple ? "--apple" : "";
      htmlLabel += ` <kbd class="shortcut ${platformClass} ${
        content.alwaysShowShortcut ? "--always-visible" : ""
      }">${translateModKey(
        PLATFORM_KEY_MODIFIER + "+" + content.shortcut,
        separator
      )}</kbd>`;
    }

    if (content.showActiveIcon) {
      htmlLabel += iconHTML("check", {
        class: "d-button-label__active-icon",
      });
    }

    return trustHTML(htmlLabel);
  }

  get convertedContent() {
    return this.args.content
      .map(this.#convertMenuOption.bind(this))
      .filter(Boolean);
  }

  get textManipulationState() {
    return this.args.context?.textManipulation?.state;
  }

  @action
  getActive(option) {
    return option.active?.({ state: this.textManipulationState });
  }

  @action
  getIcon(config) {
    if (typeof config.icon === "function") {
      return config.icon?.({ state: this.textManipulationState });
    }

    return config.icon;
  }

  get triggerLabel() {
    const label = this.args.triggerLabel;
    if (typeof label === "function") {
      return label({ state: this.textManipulationState });
    }

    return label;
  }

  <template>
    <DMenu
      @identifier={{concat "toolbar-menu__" @class}}
      @groupIdentifier="toolbar-menu"
      @onRegisterApi={{this.onRegisterApi}}
      @onShow={{@onOpen}}
      @modalForMobile={{true}}
      @placement="bottom"
      @fallbackPlacements={{array "top"}}
      @offset={{5}}
      @onKeydown={{@onKeydown}}
      tabindex="-1"
      @triggerClass={{dConcatClass "toolbar__button" @class}}
      @class="toolbar-popup-menu-options"
      title={{@title}}
    >
      <:trigger>
        {{dIcon (this.getIcon this.args)}}
        {{#if this.triggerLabel}}
          <span class="toolbar-popup-menu-options__trigger-label">
            {{this.triggerLabel}}
          </span>
        {{/if}}
      </:trigger>
      <:content>
        <DDropdownMenu {{this.trackScrollability}} as |dropdown|>
          {{#if @header}}
            <li class="dropdown-menu__header">{{@header}}</li>
          {{/if}}
          {{#each this.convertedContent as |option|}}
            <dropdown.item>
              <DButton
                @translatedLabel={{option.label}}
                @translatedTitle={{option.title}}
                @icon={{this.getIcon option}}
                @action={{fn this.onSelect option}}
                data-name={{option.name}}
                class={{dConcatClass (if (this.getActive option) "--active")}}
                aria-keyshortcuts={{option.ariaKeyshortcuts}}
              />
            </dropdown.item>
          {{/each}}
        </DDropdownMenu>
      </:content>
    </DMenu>
  </template>
}
